import {
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import useToastError from 'hooks/shared/useToastError';
import CommentsInput from 'presentation/components/modules/comments/CommentsInput';
import CommentsLoadedList from 'presentation/components/modules/comments/CommentsLoadedList';
import AppCard from 'presentation/components/shared/card/AppCard';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import FullScreenLoader from 'presentation/components/shared/FullScreenLoader';
import { Color } from 'presentation/theme/colorScheme';
import { ButtonSize } from 'presentation/theme/components/button';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAddArticleCommentMutation,
  useDeleteArticleCommentMutation,
  useFetchCommentsQuery,
  useUpdateArticleCommentMutation,
} from 'rtkQuery/sharespro/queries/articles';

interface Props {
  articleId: ID;
  isCommentable: boolean;
}

const ArticleCommentsContainer: FC<Props> = ({ articleId, isCommentable }) => {
  const [page, setPage] = useState<number>(1);

  const [text, setText] = useState<string>('');
  const [updatableCommentText, setUpdatableCommentText] = useState<string>('');
  const [updatableComment, setUpdatableComment] =
    useState<CommentListItem | null>(null);

  const { data, isFetching, error, refetch } = useFetchCommentsQuery({
    page,
    article_id: articleId,
  });

  const { t } = useTranslation();

  const toast = useToast();
  const toastErrorHandler = useToastError();

  const [addComment, { isLoading: isAddingComment }] =
    useAddArticleCommentMutation();

  const [deleteComment, { isLoading: isDeletingComment }] =
    useDeleteArticleCommentMutation();

  const [updateComment, { isLoading: isUpdatingComment }] =
    useUpdateArticleCommentMutation();

  let content = null;
  if (data) {
    content = (
      <>
        <Center mb="20px">
          <Box borderRadius="16px" bgColor="#c2eed9" px="10px" py="5px">
            <Text color={Color.Primary600} fontSize="20px" fontWeight={700} >
              {t('commentsDiscussion')}
            </Text>
          </Box>
        </Center>
        <Text fontSize="20px" mb="20px">
          <b>{t('comments')}</b> {data.itemsCount}
        </Text>
        {isCommentable && (<CommentsInput
          onSubmit={() => {
            if (text.trim()) {
              addComment({
                text,
                article_id: articleId,
              })
                .unwrap()
                .then(() => {
                  toast({
                    description: t('commentWasAdded'),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true,
                  });
                  setText('');
                })
                .catch(toastErrorHandler);
            }
          }}
          text={text}
          setText={setText}
        />)}
        <Box h="20px" />
        <CommentsLoadedList
          items={data.items}
          pageCount={data.pageCount}
          currentPage={data.currentPage}
          setPage={setPage}
          isFetching={isFetching}
          onDeleteClicked={(comment) => {
            deleteComment({
              comment_id: comment.id,
              article_id: articleId,
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('commentWasDeleted'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
              })
              .catch(toastErrorHandler);
          }}
          onEditClicked={(comment) => {
            setUpdatableComment(comment);
            setUpdatableCommentText(comment.text);
          }}
        />
      </>
    );
  } else if (error) {
    content = (
      <AppCard>
        <ErrorContainer
          error={error}
          onRetry={() => {
            refetch();
          }}
        />
      </AppCard>
    );
  }

  const resetUpdating = (): void => {
    setUpdatableComment(null);
    setUpdatableCommentText('');
  };

  const finishCommentUpdating = (): void => {
    if (commentUpdatingSubmitIsDisabled) return;
    if (!updatableComment) return;
    updateComment({
      article_id: articleId,
      comment_id: updatableComment.id,
      text: updatableCommentText,
    })
      .unwrap()
      .then(() => {
        toast({
          description: t('commentWasUpdated'),
          status: 'success',
          position: 'top-right',
          isClosable: true,
        });
        resetUpdating();
      })
      .catch(toastErrorHandler);
  };

  const commentUpdatingSubmitIsDisabled =
    updatableCommentText === updatableComment?.text ||
    !updatableCommentText.trim();

  return (
    <>
      {content}
      <FullScreenLoader
        isLoading={isAddingComment || isDeletingComment || isUpdatingComment}
      />
      <Modal isOpen={updatableComment !== null} onClose={resetUpdating}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('editComment')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              bgColor="#F5F5F5"
              border="none"
              _focus={{
                border: 'none',
                boxShadow: 'none',
              }}
              _hover={{
                border: 'none',
                boxShadow: 'none',
              }}
              borderRadius="16px"
              resize="none"
              value={updatableCommentText}
              onChange={(e) => setUpdatableCommentText(e.target.value)}
              maxLength={1000}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  !e.altKey &&
                  !e.ctrlKey &&
                  !e.metaKey
                ) {
                  e.preventDefault();
                  finishCommentUpdating();
                }
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="end">
              <Button
                disabled={commentUpdatingSubmitIsDisabled}
                onClick={finishCommentUpdating}
                size={ButtonSize.sm}
              >
                {t('save')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ArticleCommentsContainer;
